import { useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import { alpha } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { bookingApi, bookingKeys } from '@/features/booking/api/booking.api';
import { TravelerSosSheet } from '@/features/booking/components/TravelerSosSheet';
import { ContactLockedNotice } from '@/features/booking/components/ContactLockedNotice';
import { TripExtensionPanel } from '@/features/trip-extension';
import { useRefetchAtContactUnlock } from '@/features/booking/hooks/useRefetchAtContactUnlock';
import { Button } from '@/shared/components/ui';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { PRICING_TYPE_LABELS } from '@/features/booking/utils/bookingPricing';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { BookingStatus } from '@/types';
import { brand } from '@/app/theme/tokens';
import { fadeUp, staggerContainer } from '@/theme/motion';
import { formatBookingWhen } from '@/shared/utils/bookingWhen';

const HERO_IMAGE = '/images/pietro-de-grandi-T7K4aEPoGGk-unsplash.jpg';
const SIDE_IMAGE_A = '/images/bali.webp';
const SIDE_IMAGE_B = '/images/cappadocia-travel.jpg';

function useWindowSize() {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const update = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return size;
}

function DetailRow({
  icon,
  label,
  value,
  emphasize,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.15,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
          color: 'primary.main',
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: emphasize ? 700 : 600,
            color: emphasize ? 'textColors.heading' : 'textColors.body',
            fontSize: emphasize ? '1.05rem' : '0.95rem',
            mt: 0.25,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function ContactLink({
  href,
  icon,
  label,
  value,
  external,
}: {
  href: string;
  icon: ReactNode;
  label: string;
  value: string;
  external?: boolean;
}) {
  return (
    <Box
      component="a"
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.15,
        borderRadius: (t) => t.radii.md,
        border: '1px solid',
        borderColor: 'divider',
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: 'background.paper',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
        },
      }}
    >
      <Box sx={{ color: 'primary.main', display: 'flex', '& .MuiSvgIcon-root': { fontSize: 18 } }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'textColors.body',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

const confirmationActionBtnSx = {
  height: 44,
  minHeight: 44,
  maxHeight: 44,
  px: 2,
  py: 0,
  borderRadius: 2,
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: 1.2,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
} as const;

function BookingConfirmationContent() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(true);
  const [sosOpen, setSosOpen] = useState(false);

  const { data: booking, isLoading, isError, refetch, dataUpdatedAt } = useQuery({
    queryKey: bookingKeys.detail(bookingId ?? ''),
    queryFn: () => bookingApi.getById(bookingId!),
    enabled: Boolean(bookingId),
  });
  useRefetchAtContactUnlock(booking ? [booking] : undefined, refetch, dataUpdatedAt);

  const isConfirmed = booking?.status === BookingStatus.CONFIRMED;
  const canUseSos =
    booking?.status === BookingStatus.UPCOMING || booking?.status === BookingStatus.IN_PROGRESS;
  const showPaidSuccess =
    Boolean(booking) &&
    (isConfirmed ||
      booking!.status === BookingStatus.UPCOMING ||
      booking!.status === BookingStatus.IN_PROGRESS ||
      booking!.status === BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION ||
      booking!.status === BookingStatus.COMPLETED);

  useEffect(() => {
    if (!showPaidSuccess) return;
    setShowConfetti(true);
    const timer = window.setTimeout(() => setShowConfetti(false), 6500);
    return () => window.clearTimeout(timer);
  }, [showPaidSuccess, booking?.id]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '40vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isError || !booking) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Alert severity="error">Booking not found.</Alert>
        </Container>
      </Box>
    );
  }

  const guideContacts = booking.guide;
  const hasGuideContacts = Boolean(
    guideContacts?.phone || guideContacts?.whatsappNumber || guideContacts?.email
  );
  const contactsLocked = booking.contactAccess?.state === 'locked';
  const reference = `#${booking.id.slice(-8).toUpperCase()}`;
  const guideName = booking.guide?.fullName ?? 'Your guide';
  const destinationLabel = booking.destinationLabel ?? booking.destinationName ?? 'Your trip';
  const travelerEmail = user?.email?.trim();

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        background: `
          radial-gradient(ellipse 70% 45% at 10% -5%, ${alpha(brand.success, 0.12)} 0%, transparent 55%),
          radial-gradient(ellipse 55% 40% at 95% 15%, ${alpha(brand.blue400, 0.14)} 0%, transparent 50%),
          radial-gradient(ellipse 45% 30% at 50% 100%, ${alpha(brand.gold, 0.08)} 0%, transparent 45%),
          ${brand.background}
        `,
      }}
    >
      {showPaidSuccess && showConfetti && width > 0 && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={220}
          recycle={false}
          gravity={0.18}
          tweenDuration={4800}
          colors={[
            brand.blue500,
            brand.blue600,
            brand.gold,
            brand.success,
            brand.blue300,
            '#FFFFFF',
            brand.goldDark,
          ]}
          style={{ position: 'fixed', inset: 0, zIndex: 20, pointerEvents: 'none' }}
        />
      )}

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          {/* Left: celebration + imagery */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component={motion.div}
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}
            >
              <Paper
                component={motion.div}
                variants={fadeUp}
                elevation={0}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: (t) => t.radii.xl,
                  minHeight: { xs: 280, sm: 340, md: 420 },
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: `0 24px 60px ${alpha(brand.navy, 0.1)}`,
                }}
              >
                <Box
                  component="img"
                  src={HERO_IMAGE}
                  alt={destinationLabel}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `
                      linear-gradient(160deg, ${alpha(brand.navy, 0.72)} 0%, ${alpha(brand.navy, 0.35)} 45%, ${alpha(brand.navy, 0.78)} 100%)
                    `,
                  }}
                />

                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    minHeight: { xs: 280, sm: 340, md: 420 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: { xs: 3, sm: 3.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      component={motion.div}
                      initial={{ scale: 0, rotate: -20 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.15 }}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: showPaidSuccess ? brand.successLight : alpha(brand.gold, 0.9),
                        border: '2px solid',
                        borderColor: showPaidSuccess ? brand.success : brand.gold,
                        boxShadow: `0 10px 28px ${alpha(brand.navy, 0.35)}`,
                      }}
                    >
                      <CheckRoundedIcon
                        sx={{
                          fontSize: 34,
                          color: showPaidSuccess ? 'success.main' : 'secondary.dark',
                        }}
                      />
                    </Box>
                    {showPaidSuccess && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.6,
                          borderRadius: 999,
                          bgcolor: alpha('#fff', 0.16),
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          border: `1px solid ${alpha('#fff', 0.28)}`,
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        Confirmed
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      component={motion.h1}
                      variants={fadeUp}
                      sx={{
                        fontWeight: 800,
                        color: '#fff',
                        fontSize: { xs: '2rem', sm: '2.35rem' },
                        letterSpacing: '-0.03em',
                        lineHeight: 1.15,
                        mb: 1,
                      }}
                    >
                      {showPaidSuccess ? 'Congratulations!' : 'Booking received'}
                    </Typography>
                    <Typography
                      component={motion.p}
                      variants={fadeUp}
                      sx={{
                        color: alpha('#fff', 0.88),
                        fontSize: { xs: '0.95rem', sm: '1.05rem' },
                        lineHeight: 1.65,
                        maxWidth: 420,
                        mb: 2,
                      }}
                    >
                      {showPaidSuccess
                        ? 'Your adventure is booked. Payment was successful and your guide has been notified.'
                        : `Status: ${booking.status.replace(/_/g, ' ')}`}
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.85,
                        borderRadius: (t) => t.radii.md,
                        bgcolor: alpha('#fff', 0.12),
                        border: `1px solid ${alpha('#fff', 0.2)}`,
                        color: '#fff',
                      }}
                    >
                      <PlaceOutlinedIcon sx={{ fontSize: 18 }} />
                      <Typography fontWeight={700} fontSize="0.9rem">
                        {destinationLabel}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              <Grid container spacing={1.5}>
                <Grid size={6}>
                  <Box
                    component={motion.div}
                    variants={fadeUp}
                    sx={{
                      position: 'relative',
                      borderRadius: (t) => t.radii.lg,
                      overflow: 'hidden',
                      height: { xs: 110, sm: 130 },
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      component="img"
                      src={SIDE_IMAGE_A}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    component={motion.div}
                    variants={fadeUp}
                    sx={{
                      position: 'relative',
                      borderRadius: (t) => t.radii.lg,
                      overflow: 'hidden',
                      height: { xs: 110, sm: 130 },
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      component="img"
                      src={SIDE_IMAGE_B}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Paper
                component={motion.div}
                variants={fadeUp}
                elevation={0}
                sx={{
                  display: 'flex',
                  gap: 1.75,
                  alignItems: 'flex-start',
                  p: 2.25,
                  borderRadius: (t) => t.radii.lg,
                  border: '1px solid',
                  borderColor: alpha(brand.blue500, 0.28),
                  bgcolor: alpha(brand.blue50, 0.9),
                  backgroundImage: `radial-gradient(ellipse 80% 120% at 100% 0%, ${alpha(brand.blue300, 0.35)} 0%, transparent 55%)`,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: (t) => t.radii.md,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(brand.blue600, 0.12),
                    color: 'primary.main',
                  }}
                >
                  <MarkEmailReadOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800} color="textColors.heading" sx={{ mb: 0.35 }}>
                    Check your inbox
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {contactsLocked
                      ? 'Your guide’s phone and WhatsApp unlock shortly before your trip. We’ll notify you and send them to your email'
                      : 'Your booking confirmation and guide details are available here and in your email'}
                    {travelerEmail ? (
                      <>
                        {' '}
                        (
                        <Box component="span" sx={{ fontWeight: 700, color: 'textColors.body' }}>
                          {travelerEmail}
                        </Box>
                        )
                      </>
                    ) : null}
                    .
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* Right: booking summary */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              component={motion.div}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 2.5, sm: 3.25 },
                borderRadius: (t) => t.radii.xl,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: `0 20px 50px ${alpha(brand.navy, 0.06)}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 2.25,
                  pb: 2.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: (t) => t.radii.md,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
                      color: 'secondary.dark',
                      flexShrink: 0,
                    }}
                  >
                    <ConfirmationNumberOutlinedIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      Booking reference
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.125rem',
                        letterSpacing: '0.04em',
                        color: 'textColors.heading',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      {reference}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.75,
                  mb: 1,
                  borderRadius: (t) => t.radii.md,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                }}
              >
                <Avatar
                  src={booking.guide?.profilePhoto}
                  alt={guideName}
                  sx={{
                    width: 56,
                    height: 56,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    boxShadow: `0 4px 14px ${alpha(brand.navy, 0.12)}`,
                  }}
                >
                  {guideName
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((p) => p[0]?.toUpperCase() ?? '')
                    .join('')}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    Your guide
                  </Typography>
                  <Typography fontWeight={700} color="textColors.heading" noWrap>
                    {guideName}
                  </Typography>
                </Box>
              </Box>

              <Stack divider={<Divider flexItem sx={{ borderColor: 'divider' }} />} sx={{ mt: 0.5, flex: 1 }}>
                <DetailRow
                  icon={<CalendarMonthOutlinedIcon />}
                  label="When"
                  value={formatBookingWhen(booking)}
                />
                <DetailRow
                  icon={<PlaceOutlinedIcon />}
                  label="Destination"
                  value={destinationLabel}
                />
                <DetailRow
                  icon={<AccessTimeOutlinedIcon />}
                  label="Package"
                  value={PRICING_TYPE_LABELS[booking.pricingType]}
                />
                <DetailRow
                  icon={<GroupsOutlinedIcon />}
                  label="Travelers"
                  value={`${booking.travelerCount} traveler${booking.travelerCount > 1 ? 's' : ''}`}
                />
                <DetailRow
                  icon={<ConfirmationNumberOutlinedIcon />}
                  label="Total paid"
                  value={formatGuidePrice(booking.grossAmount, booking.currency) ?? '—'}
                  emphasize
                />
              </Stack>

              <Box sx={{ mt: 2.25, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                <TripExtensionPanel
                  booking={booking}
                  viewer="traveler"
                  autoOpen={searchParams.get('extra') === '1'}
                />
              </Box>

              {contactsLocked && (
                <Box sx={{ mt: 2.25, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                  <ContactLockedNotice audience="traveler" unlocksAt={booking.contactAccess?.unlocksAt} />
                </Box>
              )}

              {hasGuideContacts && guideContacts && (
                <Box sx={{ mt: 2.25, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="textColors.heading"
                    sx={{ mb: 1.25 }}
                  >
                    Guide contact
                  </Typography>
                  <Stack spacing={1}>
                    {guideContacts.phone && (
                      <ContactLink
                        href={`tel:${guideContacts.phone}`}
                        icon={<PhoneOutlinedIcon />}
                        label="Phone"
                        value={guideContacts.phone}
                      />
                    )}
                    {guideContacts.whatsappNumber && (
                      <ContactLink
                        href={`https://wa.me/${guideContacts.whatsappNumber.replace(/\D/g, '')}`}
                        icon={<PhoneOutlinedIcon />}
                        label="WhatsApp"
                        value={guideContacts.whatsappNumber}
                        external
                      />
                    )}
                    {guideContacts.email && (
                      <ContactLink
                        href={`mailto:${guideContacts.email}`}
                        icon={<EmailOutlinedIcon />}
                        label="Email"
                        value={guideContacts.email}
                      />
                    )}
                  </Stack>
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                  gap: 1.25,
                  mt: 'auto',
                  pt: 3,
                }}
              >
                {canUseSos && (
                  <Button
                    disableMotion
                    variant="outlined"
                    color="inherit"
                    onClick={() => setSosOpen(true)}
                    sx={{
                      ...confirmationActionBtnSx,
                      flex: { xs: 'none', sm: '0 0 auto' },
                      minWidth: { sm: 96 },
                      width: { xs: '100%', sm: 'auto' },
                      borderColor: brand.error,
                      color: brand.error,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      '&:hover': {
                        borderColor: '#B91C1C',
                        color: '#B91C1C',
                        bgcolor: brand.errorLight,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    SOS
                  </Button>
                )}
                <Button
                  disableMotion
                  component={RouterLink}
                  to="/my-bookings"
                  intent="secondary-outlined"
                  sx={{
                    ...confirmationActionBtnSx,
                    flex: { xs: 'none', sm: 1 },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  My bookings
                </Button>
                <Button
                  disableMotion
                  component={RouterLink}
                  to="/guides"
                  intent="primary-dark"
                  sx={{
                    ...confirmationActionBtnSx,
                    flex: { xs: 'none', sm: 1 },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Browse guides
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <TravelerSosSheet
        booking={canUseSos ? booking : null}
        open={sosOpen && canUseSos}
        onClose={() => setSosOpen(false)}
      />
    </Box>
  );
}

export function BookingConfirmationPage() {
  return (
    <ProtectedRoute>
      <BookingConfirmationContent />
    </ProtectedRoute>
  );
}
